<template>
  <el-dialog
    v-model="visible"
    :title="title"
    width="min(920px, 96vw)"
    top="4vh"
    draggable
    destroy-on-close
    class="attachment-preview-dialog"
    @closed="handleClosed"
  >
    <div v-if="previewKind === 'image' && imageObjectUrl && !error && !loading" class="attachment-preview-toolbar">
      <el-button size="small" @click="rotateImage(-90)">左旋</el-button>
      <el-button size="small" @click="rotateImage(90)">右旋</el-button>
      <span class="toolbar-divider" />
      <el-button size="small" :disabled="imageScale >= MAX_IMAGE_SCALE" @click="zoomImage(ZOOM_STEP)">放大</el-button>
      <el-button size="small" :disabled="imageScale <= MIN_IMAGE_SCALE" @click="zoomImage(-ZOOM_STEP)">缩小</el-button>
      <el-button size="small" @click="resetImageScale">100%</el-button>
      <span class="zoom-label">{{ zoomPercent }}%</span>
      <span class="toolbar-divider" />
      <el-button size="small" :disabled="!canResetImageView" @click="resetImageView">重置</el-button>
    </div>
    <div v-loading="loading" class="attachment-preview-body">
      <el-empty v-if="error" :description="error" :image-size="72" />
      <div
        v-else-if="previewKind === 'image' && imageObjectUrl"
        ref="imageWrapRef"
        class="attachment-preview-image-wrap"
        :class="{ 'is-draggable': imageScale > 1, 'is-dragging': isPanning }"
        @wheel.prevent="handleImageWheel"
        @mousedown="startPan"
      >
        <img
          :src="imageObjectUrl"
          :alt="fileName"
          class="attachment-preview-image"
          :style="imageTransformStyle"
          draggable="false"
          @load="handleImageLoad"
        />
      </div>
      <div
        v-else-if="previewKind === 'pdf'"
        ref="pdfContainerRef"
        class="attachment-preview-pdf"
      />
    </div>
    <template #footer>
      <el-button @click="visible = false">关闭</el-button>
      <el-button type="primary" :disabled="!fileUrl" @click="handleDownload">下载</el-button>
    </template>
  </el-dialog>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import {
  attachmentPreviewState,
  closeAttachmentPreview,
  fetchAttachmentInlineBlob,
  getAttachmentPreviewKind
} from '@/utils/attachmentPreview'
import { downloadAttachment } from '@/utils/attachmentDownload'
import { renderPdfToContainer } from '@/utils/processGuidePdf'

const MIN_IMAGE_SCALE = 0.25
const MAX_IMAGE_SCALE = 4
const ZOOM_STEP = 0.25

export default {
  name: 'AttachmentPreviewDialog',
  setup() {
    const loading = ref(false)
    const error = ref('')
    const imageObjectUrl = ref('')
    const imageRotation = ref(0)
    const imageScale = ref(1)
    const imagePanX = ref(0)
    const imagePanY = ref(0)
    const isPanning = ref(false)
    const imageWrapRef = ref(null)
    const pdfContainerRef = ref(null)
    let pdfHandle = null
    let revokeUrl = ''
    let panSession = null

    const visible = computed({
      get: () => attachmentPreviewState.visible,
      set: (val) => {
        if (!val) closeAttachmentPreview()
      }
    })

    const fileUrl = computed(() => attachmentPreviewState.fileUrl)
    const fileName = computed(() => attachmentPreviewState.fileName || '附件')
    const previewKind = computed(() => getAttachmentPreviewKind(fileName.value))
    const title = computed(() => `预览：${fileName.value}`)

    const zoomPercent = computed(() => Math.round(imageScale.value * 100))

    const canResetImageView = computed(() =>
      imageRotation.value !== 0
      || imageScale.value !== 1
      || imagePanX.value !== 0
      || imagePanY.value !== 0
    )

    const imageTransformStyle = computed(() => ({
      transform: `translate(${imagePanX.value}px, ${imagePanY.value}px) rotate(${imageRotation.value}deg) scale(${imageScale.value})`
    }))

    const clampScale = (scale) => Math.min(MAX_IMAGE_SCALE, Math.max(MIN_IMAGE_SCALE, scale))

    const rotateImage = (delta) => {
      imageRotation.value = (imageRotation.value + delta + 360) % 360
    }

    const zoomImage = (delta) => {
      imageScale.value = clampScale(Number((imageScale.value + delta).toFixed(2)))
    }

    const resetImageScale = () => {
      imageScale.value = 1
      imagePanX.value = 0
      imagePanY.value = 0
    }

    const resetImageView = () => {
      imageRotation.value = 0
      resetImageScale()
    }

    const handleImageWheel = (event) => {
      const delta = event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP
      zoomImage(delta)
    }

    const stopPan = () => {
      if (!panSession) return
      panSession = null
      isPanning.value = false
      window.removeEventListener('mousemove', onPanMove)
      window.removeEventListener('mouseup', stopPan)
    }

    const onPanMove = (event) => {
      if (!panSession) return
      imagePanX.value = panSession.originPanX + (event.clientX - panSession.startX)
      imagePanY.value = panSession.originPanY + (event.clientY - panSession.startY)
    }

    const startPan = (event) => {
      if (event.button !== 0 || imageScale.value <= 1) return
      event.preventDefault()
      panSession = {
        startX: event.clientX,
        startY: event.clientY,
        originPanX: imagePanX.value,
        originPanY: imagePanY.value
      }
      isPanning.value = true
      window.addEventListener('mousemove', onPanMove)
      window.addEventListener('mouseup', stopPan)
    }

    const handleImageLoad = () => {
      resetImageView()
    }

    const destroyPdf = () => {
      if (pdfHandle) {
        pdfHandle.destroy()
        pdfHandle = null
      }
    }

    const clearObjectUrl = () => {
      if (revokeUrl) {
        URL.revokeObjectURL(revokeUrl)
        revokeUrl = ''
      }
      imageObjectUrl.value = ''
    }

    const resetPreview = () => {
      stopPan()
      loading.value = false
      error.value = ''
      imageRotation.value = 0
      imageScale.value = 1
      imagePanX.value = 0
      imagePanY.value = 0
      destroyPdf()
      clearObjectUrl()
      if (pdfContainerRef.value) {
        pdfContainerRef.value.innerHTML = ''
      }
    }

    const loadPreview = async () => {
      if (!attachmentPreviewState.visible || !attachmentPreviewState.fileUrl) return
      resetPreview()
      loading.value = true
      try {
        const { blob } = await fetchAttachmentInlineBlob(
          attachmentPreviewState.fileUrl,
          attachmentPreviewState.fileName
        )
        if (previewKind.value === 'image') {
          revokeUrl = URL.createObjectURL(blob)
          imageObjectUrl.value = revokeUrl
          return
        }
        if (previewKind.value === 'pdf') {
          const buffer = await blob.arrayBuffer()
          await nextTick()
          if (!pdfContainerRef.value) {
            throw new Error('预览容器不存在')
          }
          pdfHandle = await renderPdfToContainer(buffer, pdfContainerRef.value)
        }
      } catch (e) {
        error.value = e?.message || '预览失败'
      } finally {
        loading.value = false
      }
    }

    watch(
      () => [attachmentPreviewState.visible, attachmentPreviewState.fileUrl],
      ([isVisible]) => {
        if (isVisible) {
          loadPreview()
        }
      }
    )

    const handleClosed = () => {
      resetPreview()
      attachmentPreviewState.fileUrl = ''
      attachmentPreviewState.fileName = ''
    }

    const handleDownload = () => {
      downloadAttachment(fileUrl.value, fileName.value)
    }

    onBeforeUnmount(() => {
      stopPan()
    })

    return {
      visible,
      loading,
      error,
      imageObjectUrl,
      imageWrapRef,
      pdfContainerRef,
      previewKind,
      title,
      fileUrl,
      fileName,
      imageRotation,
      imageScale,
      imageTransformStyle,
      zoomPercent,
      canResetImageView,
      isPanning,
      MIN_IMAGE_SCALE,
      MAX_IMAGE_SCALE,
      ZOOM_STEP,
      rotateImage,
      zoomImage,
      resetImageScale,
      resetImageView,
      handleImageWheel,
      startPan,
      handleImageLoad,
      handleClosed,
      handleDownload
    }
  }
}
</script>

<style scoped>
.attachment-preview-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
}

.toolbar-divider {
  width: 1px;
  height: 20px;
  background: #dcdfe6;
  margin: 0 2px;
}

.zoom-label {
  min-width: 44px;
  color: #606266;
  font-size: 13px;
  text-align: center;
}

.attachment-preview-body {
  min-height: 320px;
  max-height: 76vh;
  overflow: auto;
}

.attachment-preview-image-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  padding: 8px;
  overflow: hidden;
  user-select: none;
}

.attachment-preview-image-wrap.is-draggable {
  cursor: grab;
}

.attachment-preview-image-wrap.is-dragging {
  cursor: grabbing;
}

.attachment-preview-image {
  display: block;
  max-width: 100%;
  max-height: 70vh;
  margin: 0 auto;
  transform-origin: center center;
  transition: transform 0.15s ease;
  pointer-events: none;
}

.attachment-preview-image-wrap.is-dragging .attachment-preview-image {
  transition: none;
}

.attachment-preview-pdf {
  min-height: 300px;
  padding: 12px;
  background: #f0f2f5;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  box-sizing: border-box;
}

.attachment-preview-pdf :deep(.process-guide-pdf-page) {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}
</style>
